import { Trans, useTranslation } from "react-i18next";
import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react-pro";
import { useCartStore, type StrategyCartItem } from "../../../state/cartStore";
import { useToast } from "../../../context/useToast";

interface PortfolioCartHoldsUpgradeDialogProps {
  /** The strategies she tried to add, or null when the question isn't being asked. */
  items: StrategyCartItem[] | null;
  onClose: () => void;
}

/**
 * Asked when Add to cart is pressed while an upgrade or an extension is in the cart, either of which is
 * checked out on its own. The strategy page's CartHoldsUpgradeDialog, for several strategies at once.
 */
export function PortfolioCartHoldsUpgradeDialog({ items, onClose }: PortfolioCartHoldsUpgradeDialogProps) {
  const { t } = useTranslation("portfolioBuilder");
  const upgrade = useCartStore((s) => s.upgrade);
  const extension = useCartStore((s) => s.extension);
  const removeUpgrade = useCartStore((s) => s.removeUpgrade);
  const removeExtension = useCartStore((s) => s.removeExtension);
  const addStrategy = useCartStore((s) => s.addStrategy);
  const { showSuccess } = useToast();

  const count = items?.length ?? 0;

  const emptyAndAdd = () => {
    if (items === null) {
      return;
    }

    removeUpgrade();
    removeExtension();
    items.forEach((item) => addStrategy(item));
    showSuccess(t("panel.added", { count }));
    onClose();
  };

  return (
    <CModal visible={items !== null} onClose={onClose} alignment="center">
      <CModalHeader>
        <CModalTitle>{t("cartHoldsUpgrade.title", { count })}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <p className="mb-0">
          {extension !== null ? (
            <Trans
              t={t}
              i18nKey="cartHoldsUpgrade.extension"
              values={{ name: extension.name }}
              components={{ strong: <strong /> }}
            />
          ) : (
            <Trans
              t={t}
              i18nKey="cartHoldsUpgrade.upgrade"
              values={{ name: upgrade?.name }}
              components={{ strong: <strong /> }}
            />
          )}
        </p>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onClose}>
          {t("cartHoldsUpgrade.keep")}
        </CButton>
        <CButton color="primary" onClick={emptyAndAdd}>
          {t("cartHoldsUpgrade.emptyAndAdd")}
        </CButton>
      </CModalFooter>
    </CModal>
  );
}
